/** The intervention card: engine suggestions as one-tap buttons, recommended first; a hint
 * for the most-chosen wrong option with its misconception tag. STORY-035 adds the AI-drafted
 * explanation into the same card. Nothing here acts automatically. */
import { useTranslation } from "react-i18next";

import type { Snapshot } from "../live/useLive";
import { Button, DraftFrame } from "../ui";

type Suggestion = NonNullable<Snapshot["suggestions"]>[number];

export function SuggestionCard({
  suggestions,
  aggregate,
  item,
  onIntervene,
  onRevote,
  onShowProjector,
  draft,
  onLaunchScaffold,
  quiet = false,
  onExplain,
}: {
  suggestions: Suggestion[];
  aggregate: Snapshot["aggregate"];
  item: NonNullable<Snapshot["item"]>["snapshot"];
  onIntervene: (kind: string, extra?: Record<string, unknown>) => void;
  onRevote: () => void;
  onShowProjector: () => void;
  draft?: {
    explanation: string;
    misconception?: string;
    kind: "draft" | "cached";
    scaffold?: { stem: string; options: { key: string; label?: string; correct?: boolean }[] } | null;
  } | null;
  onLaunchScaffold?: () => void;
  /** Quiet view (the live screen's default): the recommended action only, and one line that
   * offers the rest. Nothing is disabled — everything is one key or one tap away. */
  quiet?: boolean;
  onExplain?: () => void;
}) {
  const { t } = useTranslation();
  const hint = suggestions.find((s) => s.kind === "hint");
  const all = suggestions.filter((s) => s.kind !== "hint");
  const actions = quiet ? all.filter((s) => s.recommended).slice(0, 1) : all;
  const wrong = aggregate?.options.filter((o) => !o.correct && o.count > 0) ?? [];
  return (
    <section className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-label={t("live.suggestions")}>
      {hint && !quiet && (
        <HintBox draft={draft}>
          <p className="font-medium">{hint.reason}{(draft?.misconception ?? hint.misconception) ? `: ${draft?.misconception ?? hint.misconception}` : ""}</p>
          {draft?.explanation && <p className="mt-2">{draft.explanation}</p>}
          {!draft && wrong.length > 0 && (
            <ul className="mt-2 text-sm text-[color:var(--ink-2)]">
              {wrong.map((o) => (
                <li key={o.key}>{o.key}: {o.count}{o.misconception ? ` · ${o.misconception}` : ""}</li>
              ))}
            </ul>
          )}
          {draft?.scaffold && (
            <p className="mt-2 text-sm text-[color:var(--ink-2)]">{t("live.scaffold")}: {draft.scaffold.stem}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={onShowProjector}>{t("live.showOnProjector")}</Button>
            {onLaunchScaffold && (
              <Button variant="secondary" onClick={onLaunchScaffold}>{t("live.actions.follow_up_item")}</Button>
            )}
          </div>
        </HintBox>
      )}
      <div className="flex flex-col gap-2">
        {actions.map((s) => (
          <Button
            key={s.kind}
            variant={s.recommended ? "primary" : "secondary"}
            block
            onClick={() => {
              onIntervene(s.kind, { suggestion_kind: s.kind, reason: s.reason, item_id: item.item_id });
              if (s.kind === "peer_discuss") onRevote();
            }}
          >
            {t(`live.actions.${s.kind}`)}{s.reason ? <span className="ml-2 text-sm opacity-80">· {s.reason}</span> : null}
          </Button>
        ))}
        {actions.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("live.tooFew")}</p>}
        {quiet && (hint || all.length > 1) && onExplain && (
          <Button variant="tertiary" onClick={onExplain} aria-keyshortcuts="E">{t("live.explain")}</Button>
        )}
      </div>
    </section>
  );
}

/** The engine's own hint is plain; only a model draft earns the violet frame. */
function HintBox({ draft, children }: { draft: { kind: "draft" | "cached" } | null | undefined; children: React.ReactNode }) {
  if (draft) return <DraftFrame kind={draft.kind}>{children}</DraftFrame>;
  return <div className="rounded-[var(--radius-card)] border p-4">{children}</div>;
}
